/**
 * DevAdminItpCall — 통화 링크(통역 통화) 운영 설정.
 *
 * 저장소 = admin/itp 단일 문서 (rules 상 superAdmin 전용 — API 키가 담기므로
 * admin/config 처럼 인증 유저 read 를 열면 안 된다):
 *  · allowedUids: 통화 링크 기능 허용 유저 — 서버 게이트(createItpCallLink)의 유일한 판정 소스
 *  · geminiApiKey: 실시간 통역 키 오버라이드 — 비우면 Secret Manager 기본값
 *
 * 허용 지정은 setItpCallAccess callable 경유 — users/{uid}.itpCallEnabled 미러(UI 노출용)를
 * 함께 갱신해야 하는데 users 쓰기는 rules 상 본인 전용이라 클라 직접 쓰기가 불가능하다.
 */
import React, { useEffect, useState } from 'react';
import { collection, deleteField, doc, getDoc, getDocs, limit, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { AdminFire, getAdminFunc } from '../../services/SuperAdminFirebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { KeyRound, Loader2, Phone, Search, ShieldCheck, Trash2, UserPlus } from 'lucide-react';

interface ItpAdminDoc { allowedUids?: string[]; geminiApiKey?: string; }
interface UserRow { id: string; name?: string; email?: string; }

const DevAdminItpCall: React.FC = () => {
    const [cfg, setCfg] = useState<ItpAdminDoc>({});
    const [allowedUsers, setAllowedUsers] = useState<UserRow[]>([]);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<UserRow[]>([]);
    const [searching, setSearching] = useState(false);
    const [mutating, setMutating] = useState('');   // 처리 중인 uid
    const [keyDraft, setKeyDraft] = useState('');
    const [keySaving, setKeySaving] = useState(false);
    const [error, setError] = useState('');

    // 설정 문서 실시간 구독 (superAdmin claim 으로 rules 통과)
    useEffect(() => {
        return onSnapshot(doc(AdminFire.store, 'admin', 'itp'),
            (snap) => setCfg((snap.data() as ItpAdminDoc) ?? {}),
            (e) => setError(`설정 로드 실패: ${e.message}`));
    }, []);

    // 허용 uid → 이름/이메일 (목록이 작아 개별 getDoc 로 충분)
    useEffect(() => {
        const uids = cfg.allowedUids ?? [];
        let stale = false;
        Promise.all(uids.map(async (uid) => {
            const s = await getDoc(doc(AdminFire.store, 'users', uid)).catch(() => null);
            const d = s?.data() as { name?: string; email?: string } | undefined;
            return { id: uid, name: d?.name, email: d?.email };
        })).then((rows) => { if (!stale) setAllowedUsers(rows); });
        return () => { stale = true; };
    }, [cfg.allowedUids?.join(',')]);

    const runSearch = async () => {
        const key = search.trim();
        if (!key) return;
        setSearching(true);
        setError('');
        try {
            const users = collection(AdminFire.store, 'users');
            const [byEmail, byName] = await Promise.all([
                getDocs(query(users, where('email', '==', key), limit(10))),
                getDocs(query(users, where('name', '>=', key), where('name', '<=', key + ''), limit(10))),
            ]);
            const map = new Map<string, UserRow>();
            [...byEmail.docs, ...byName.docs].forEach((d) => {
                const v = d.data() as { name?: string; email?: string };
                map.set(d.id, { id: d.id, name: v.name, email: v.email });
            });
            setResults([...map.values()]);
            if (map.size === 0) setError('검색 결과가 없습니다 (이메일은 정확히, 이름은 앞부분 일치)');
        } catch (e: any) {
            setError(`검색 실패: ${e.message}`);
        } finally {
            setSearching(false);
        }
    };

    const setAccess = async (userId: string, enabled: boolean) => {
        setMutating(userId);
        setError('');
        try {
            await getAdminFunc('setItpCallAccess')({ userId, enabled });
        } catch (e: any) {
            setError(`변경 실패: ${e.message}`);
        } finally {
            setMutating('');
        }
    };

    const saveKey = async () => {
        const v = keyDraft.trim();
        if (!v) return;
        // Gemini API 키 형식(AIza…)이 아니면 실수 방지 확인 — 형식이 다른 키도 존재할 수 있어 차단은 안 함
        if (!v.startsWith('AIza') && !window.confirm('일반적인 Gemini API 키 형식(AIza…)이 아닙니다. 그래도 저장할까요?')) return;
        setKeySaving(true);
        setError('');
        try {
            await setDoc(doc(AdminFire.store, 'admin', 'itp'), { geminiApiKey: v }, { merge: true });
            setKeyDraft('');
        } catch (e: any) {
            setError(`키 저장 실패: ${e.message}`);
        } finally {
            setKeySaving(false);
        }
    };

    const clearKey = async () => {
        if (!window.confirm('오버라이드 키를 삭제하고 Secret Manager 기본값으로 되돌릴까요?')) return;
        setKeySaving(true);
        try {
            await updateDoc(doc(AdminFire.store, 'admin', 'itp'), { geminiApiKey: deleteField() });
        } catch (e: any) {
            setError(`키 삭제 실패: ${e.message}`);
        } finally {
            setKeySaving(false);
        }
    };

    const allowedSet = new Set(cfg.allowedUids ?? []);
    const masked = cfg.geminiApiKey
        ? `${'•'.repeat(12)}${cfg.geminiApiKey.slice(-4)}`
        : null;

    return (
        <div className="space-y-6 p-6">
            <div>
                <h1 className="flex items-center gap-2 text-2xl font-bold"><Phone className="h-6 w-6" /> 통화 링크</h1>
                <p className="text-sm text-muted-foreground">통역 통화(통화 링크) 기능 허용 유저와 Gemini API 키를 관리합니다.</p>
            </div>

            {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="h-5 w-5" /> 허용 유저</CardTitle>
                    <CardDescription>
                        여기 지정된 유저만 내 채널에서 통화 링크를 만들 수 있습니다. 판정은 서버(admin/itp)가
                        하므로 클라이언트 위조로는 우회되지 않습니다.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {allowedUsers.length === 0
                        ? <p className="text-sm text-muted-foreground">아직 허용된 유저가 없습니다 — 아무도 통화 링크를 만들 수 없는 상태입니다.</p>
                        : (
                            <div className="space-y-1.5">
                                {allowedUsers.map((u) => (
                                    <div key={u.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                                        <span className="min-w-0 flex-1 truncate text-sm">
                                            {u.name ?? '(이름 없음)'} <span className="text-muted-foreground">{u.email ?? u.id}</span>
                                        </span>
                                        <Button variant="outline" size="sm" disabled={mutating === u.id}
                                            onClick={() => setAccess(u.id, false)}>
                                            {mutating === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                            <span className="ml-1">해제</span>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                    <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); void runSearch(); }}>
                        <input value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="이메일(정확히) 또는 이름(앞부분)으로 검색"
                            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-sm" />
                        <Button type="submit" variant="outline" size="sm" disabled={searching || !search.trim()}>
                            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            <span className="ml-1">검색</span>
                        </Button>
                    </form>

                    {results.length > 0 && (
                        <div className="space-y-1.5">
                            {results.map((u) => (
                                <div key={u.id} className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2">
                                    <span className="min-w-0 flex-1 truncate text-sm">
                                        {u.name ?? '(이름 없음)'} <span className="text-muted-foreground">{u.email ?? u.id}</span>
                                    </span>
                                    {allowedSet.has(u.id)
                                        ? <Badge variant="secondary">허용됨</Badge>
                                        : (
                                            <Button variant="outline" size="sm" disabled={mutating === u.id}
                                                onClick={() => setAccess(u.id, true)}>
                                                {mutating === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                                                <span className="ml-1">허용</span>
                                            </Button>
                                        )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg"><KeyRound className="h-5 w-5" /> Gemini API 키 (실시간 통역)</CardTitle>
                    <CardDescription>
                        통역 세션(issueItpLease)과 dev 시드가 쓰는 키입니다. 오버라이드를 저장하면 즉시 적용되고,
                        비우면 Secret Manager 의 GEMINI_API_KEY 를 사용합니다. 키는 슈퍼어드민 외에는 읽을 수 없습니다.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">현재:</span>
                        {masked
                            ? <><code className="rounded bg-muted px-2 py-0.5">{masked}</code><Badge>오버라이드 사용 중</Badge></>
                            : <Badge variant="secondary">Secret Manager 기본값</Badge>}
                        {masked && (
                            <Button variant="outline" size="sm" disabled={keySaving} onClick={() => { void clearKey(); }}>
                                기본값으로 되돌리기
                            </Button>
                        )}
                    </div>
                    <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); void saveKey(); }}>
                        <input value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} type="password"
                            placeholder="새 Gemini API 키 (AIza…)" autoComplete="off"
                            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-3 font-mono text-sm" />
                        <Button type="submit" size="sm" disabled={keySaving || !keyDraft.trim()}>
                            {keySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : '저장'}
                        </Button>
                    </form>
                    <p className="text-xs text-muted-foreground">
                        진행 중인 통역 세션은 기존 키로 유지되고, 다음 세션(리스 발급)부터 새 키가 적용됩니다.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default DevAdminItpCall;
